import PropTypes from 'prop-types';
import styled from 'styled-components';
import Link from 'next/link';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import StartBanner from '../StartBanner';
import JoinBanner from '../JoinBanner';

export const tenBestReasonsToIncorporateContents = [
  'Limited Liability Protection',
  'Tax Advantages',
  'Credibility and Brand Protection',
  'Raising Capital',
  'Perpetual Existence ',
  'Transferability of Ownership',
  'Building Business Credit',
  'Retirement Savings',
  'Maximize Business Deductions',
  'Additional Benefits',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 50%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const TenBestReasonsToIncorporateArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        Incorporating your business can be a game-changer, providing numerous
        benefits that can help your business thrive. Whether you&apos;re a small
        business owner or an entrepreneur with big ambitions, understanding the
        advantages of incorporation can help you make an informed decision.
      </Layout.Card.Text>
      <Layout.Card.Text>
        In this article, we&apos;ll explore the&nbsp;
        <b>top 10 reasons to incorporate your business today</b>, offering
        insights into how incorporation can protect your assets, save you money,
        and enhance your business credibility.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          1. Limited Liability Protection
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Safeguarding Personal Assets
        </Layout.Card.Text>
        <Layout.Card.Text>
          One of the primary benefits of incorporation is the limited liability
          protection it offers. Incorporation creates a separate legal entity,
          meaning your personal assets—such as your home, car, and personal
          savings—are protected from business debts and liabilities. If your
          business faces legal action or bankruptcy, your personal assets are
          generally not at risk.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          2. Tax Advantages
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Potential Tax Savings
        </Layout.Card.Text>
        <Layout.Card.Text>
          Incorporating your business can provide significant tax benefits.
          Corporations often enjoy lower tax rates and can take advantage of
          various tax deductions and credits not available to sole
          proprietorships or partnerships. Additionally, S corporations allow
          income to pass through to shareholders, avoiding double taxation at
          the corporate and individual levels.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          3. Credibility and Brand Perception
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Enhancing Professional Image
        </Layout.Card.Text>
        <Layout.Card.Text>
          Incorporation can significantly enhance your business&apos;s
          credibility and professional image. Customers, suppliers, and
          investors often perceive incorporated businesses as more stable and
          trustworthy. This increased credibility can lead to more business
          opportunities, partnerships, and better financing options.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${3}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          4. Raising Capital
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Attracting Investors
        </Layout.Card.Text>
        <Layout.Card.Text>
          Incorporated businesses have an easier time attracting investors
          compared to unincorporated entities. Investors are more likely to
          invest in a corporation due to the limited liability protection and
          the potential for a return on investment through dividends and stock
          appreciation. This access to capital can fuel growth and expansion.
        </Layout.Card.Text>
        <StartBanner />
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${4}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          5. Perpetual Existence
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Ensuring Business Continuity
        </Layout.Card.Text>
        <Layout.Card.Text>
          A corporation has perpetual existence, meaning it continues to exist
          even if the owners or shareholders change. This stability is
          beneficial for long-term business planning and can provide reassurance
          to employees, customers, and investors. It also makes it easier to
          transfer ownership through the sale of stock.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${5}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          6. Transferability of Ownership
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Simplifying Ownership Transfer
        </Layout.Card.Text>
        <Layout.Card.Text>
          Incorporating your business simplifies the transfer of ownership.
          Shares of stock can be easily bought, sold, or transferred, making it
          straightforward to bring in new investors or sell the business. This
          flexibility can be crucial for succession planning and ensuring the
          business&apos;s longevity.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${6}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          7. Building Business Credit
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Separating Personal and Business Credit
        </Layout.Card.Text>
        <Layout.Card.Text>
          Incorporation helps establish a clear distinction between personal and
          business finances. This separation is crucial for building business
          credit, which can lead to better loan terms and credit opportunities.
          Strong business credit can enhance your ability to secure financing
          for expansion, equipment purchases, and other business needs.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${7}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          8. Retirement Savings
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Offering Retirement Plans
        </Layout.Card.Text>
        <Layout.Card.Text>
          Corporations can offer a variety of retirement plans, such as 401(k)
          plans, with tax-deferred benefits. These plans can help business
          owners and employees save for retirement while enjoying tax
          advantages. Offering competitive retirement plans can also attract and
          retain top talent.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${8}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          9. Maximizing Business Deductions
        </Layout.Card.Text>
        <Layout.Card.Text>
          Incorporated businesses can deduct various business expenses, reducing
          their taxable income. Common deductions include salaries, rent,
          utilities, and health insurance premiums. These deductions can
          significantly lower your overall tax liability, allowing you to
          reinvest more profits into the business.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${9}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          10. Additional Benefits
        </Layout.Card.Text>
        <Layout.Card.Text as="h2" boldest noBottomSpace>
          Flexibility and Simplification
        </Layout.Card.Text>
        <Layout.Card.Text>
          Incorporation offers additional benefits such as operational
          flexibility and simplified business operations. Corporations can have
          multiple classes of stock, allowing for different voting rights and
          profit-sharing arrangements. This flexibility can help tailor the
          business structure to meet specific needs and goals.
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Incorporating your business provides a range of benefits, from
        protecting your personal assets to offering tax advantages and enhancing
        your business credibility. By understanding these benefits, you can make
        an informed decision about the future of your business. Incorporation
        can pave the way for growth, stability, and long-term success.
      </Layout.Card.Text>
      <Layout.Card.Text>
        For a seamless and hassle-free incorporation process, consider
        using&nbsp;<Link href="/">Incorpified</Link>. Our expert team will guide
        you through each step, ensuring your business is set up correctly and
        efficiently.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

TenBestReasonsToIncorporateArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default TenBestReasonsToIncorporateArticle;
